/**
 * The confirmation gate contract (PRD D1–D4; increment-4 plan §2.2/§6 d2–d4;
 * ADR-0015) — the shape of the record's central clinical act: per-exercise
 * outcomes with PER-VALUE provenance, and the sign-off that seals them.
 *
 * The load-bearing facts, fixed here so no surface can drift on them:
 *   - Capture is not input (D1/D2): a confirmed value's provenance says how it
 *     entered — the physio's own taps, a typed entry, or a confirmed edit —
 *     and own-tap values must name the tap rows they came from. Camera-derived
 *     values are reference BESIDE fields, never inside this structure.
 *   - CONFIRMED is carried by the sign-off record itself (§6 d2); DRAFT is the
 *     derived state ended-without-sign-off. Call lifecycle and record
 *     lifecycle are different machines — nothing here touches session status.
 *   - Sign-off is an ONLINE act (§6 d4): the request carries the device clock
 *     (`deviceNow`) so the server can record a clock-offset observation on the
 *     sign-off row — the increment-3 skew finding, homed.
 *   - Duplicate taps are never deduplicated silently (#48 review): the server
 *     surfaces CANDIDATES; the physio reconciles by appending a superseding
 *     tap row (provenance 'confirmed-edit') through migration 004's reserved
 *     seam. Both rows remain forever; disagreement is visible data.
 */

import { z } from 'zod';

import { protocolSelectionSchema } from './protocolSelection';
import { SESSION_END_DISPOSITIONS } from './session';
import { SET_SCOPED_TAP_KINDS, TAP_KINDS, exerciseCodeShapeSchema, type TapKind } from './taps';

// ---- per-value provenance (D2, §6 d3) ---------------------------------------

/**
 * How a CONFIRMED value entered. Deliberately narrower than EVENT_PROVENANCES:
 * a confirmed value is a human act — 'system' cannot confirm anything.
 */
export const CONFIRMED_VALUE_PROVENANCES = ['own-tap', 'typed-entry', 'confirmed-edit'] as const;
export type ConfirmedValueProvenance = (typeof CONFIRMED_VALUE_PROVENANCES)[number];

/**
 * One confirmed field value with its provenance and the tap rows it came from.
 * The IEC per-value query ("who entered this, when, how") is a jsonb path read
 * over exactly this shape (ADR-0010 extension).
 */
export const confirmedValueSchema = z
  .object({
    value: z.union([z.number().finite(), z.string().max(500), z.boolean()]),
    provenance: z.enum(CONFIRMED_VALUE_PROVENANCES),
    /** The session_taps rows this value derives from. */
    sourceTapIds: z.array(z.uuid()).max(200),
  })
  .superRefine((v, ctx) => {
    // The coupling IS the guarantee: an own-tap value with no source taps is
    // an unfounded claim; a typed entry citing taps is a mislabeled edit.
    if (v.provenance === 'own-tap' && v.sourceTapIds.length === 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'own-tap provenance must name its source taps',
        path: ['sourceTapIds'],
      });
    }
    if (v.provenance === 'typed-entry' && v.sourceTapIds.length > 0) {
      ctx.addIssue({
        code: 'custom',
        message: 'typed-entry provenance cannot cite source taps — that is a confirmed-edit',
        path: ['sourceTapIds'],
      });
    }
  });
export type ConfirmedValue = z.infer<typeof confirmedValueSchema>;

// ---- the tap-count proposal (D4c) -------------------------------------------

/**
 * How the OUTCOME choice itself was made (D4c, migration 010): the physio
 * accepted the tap-count proposal in one tap, or set the chip by hand. The
 * IEC note keeps provenance for every recorded value — the outcome is one.
 */
export const OUTCOME_PROVENANCES = ['proposal-accepted', 'hand-set'] as const;
export type OutcomeProvenance = (typeof OUTCOME_PROVENANCES)[number];

/**
 * The proposal shown BESIDE the outcome chips (D4c) — and STORED with the
 * record (model-D1: both are stored, so disagreement is visible data). Its
 * one label is `tap-count-met`: the trail's counts against the planned dose,
 * NEVER "criteria met" — hold quality and movement validity are clinical
 * judgments no tap count can make. Pain scoping (the PR2 anchoring): a pain
 * mark on the exercise withholds one-tap accept for that exercise; an
 * unanchored pain mark conservatively withholds the whole batch.
 */
export const tapCountProposalSchema = z.object({
  kind: z.literal('tap-count-met'),
  /** Did the (duplicate-resolved) trail meet the planned dose? */
  met: z.boolean(),
  setsFromTaps: z.number().int().min(0),
  repsFromTaps: z.number().int().min(0),
  plannedSets: z.number().int().min(1),
  plannedReps: z.number().int().min(1),
  /** Why one-tap accept was withheld (pain scoping), or null. */
  disabledBy: z.enum(['exercise-pain', 'unanchored-pain']).nullable(),
});
export type TapCountProposal = z.infer<typeof tapCountProposalSchema>;

// ---- per-exercise outcome (D1, D3) ------------------------------------------

export const EXERCISE_OUTCOMES = ['completed', 'partial', 'skipped'] as const;
export type ExerciseOutcome = (typeof EXERCISE_OUTCOMES)[number];

/**
 * The TRUE granularity the record was made at (D3): 'per-set' when the value
 * trail is the during-call tap stream; 'session-level' when the exercise went
 * untapped and the physio entered a coarse after-the-fact answer. Recorded,
 * never inferred — a session-level entry must not masquerade as set data.
 */
export const ANNOTATION_MODES = ['per-set', 'session-level'] as const;
export type AnnotationMode = (typeof ANNOTATION_MODES)[number];

export const exerciseOutcomeInputSchema = z
  .object({
    exerciseCode: exerciseCodeShapeSchema,
    outcome: z.enum(EXERCISE_OUTCOMES),
    /** Why, in the physio's words. Required unless completed. */
    reason: z.string().min(1).max(500).optional(),
    annotationMode: z.enum(ANNOTATION_MODES),
    /** How the outcome choice was made (D4c). */
    outcomeProvenance: z.enum(OUTCOME_PROVENANCES),
    /**
     * The proposal that was SHOWN for this exercise (D4c: stored beside the
     * answer so disagreement is queryable data). Null when none existed — an
     * untapped exercise has no trail to propose from.
     */
    proposal: tapCountProposalSchema.nullable(),
    /** Field name → confirmed value. May be empty for a skipped exercise. */
    confirmedValues: z.record(z.string().min(1).max(64), confirmedValueSchema),
  })
  .superRefine((o, ctx) => {
    if (o.outcome !== 'completed' && !o.reason) {
      ctx.addIssue({
        code: 'custom',
        message: `${o.outcome} requires a reason`,
        path: ['reason'],
      });
    }
    // An acceptance must have had something acceptable to accept: a met,
    // un-withheld proposal, and 'completed' is the only thing it proposes.
    // Anything else claiming proposal-accepted is a client bug.
    if (o.outcomeProvenance === 'proposal-accepted') {
      if (!o.proposal?.met || o.proposal.disabledBy != null) {
        ctx.addIssue({
          code: 'custom',
          message: 'proposal-accepted requires a met, un-withheld proposal',
          path: ['outcomeProvenance'],
        });
      }
      if (o.outcome !== 'completed') {
        ctx.addIssue({
          code: 'custom',
          message: 'the tap-count proposal only ever proposes completed',
          path: ['outcomeProvenance'],
        });
      }
    }
  });
export type ExerciseOutcomeInput = z.infer<typeof exerciseOutcomeInputSchema>;

// ---- duplicate-tap candidates + reconciliation (§6 d3) ----------------------

/**
 * Per-kind windows for near-duplicate DETECTION (candidate surfacing only —
 * the physio decides). Tight where legitimate cadence is fast (log-rep runs at
 * seconds apart), wider where a repeat almost certainly means "did that
 * register?" (a second start-set two seconds later). `undo` has no window by
 * construction: candidates are computed over CURRENT statements
 * (resolveCurrentTaps), which undo rows are never part of.
 */
export const DUPLICATE_TAP_WINDOWS_MS: Readonly<Record<Exclude<TapKind, 'undo'>, number>> = {
  'start-set': 3000,
  'log-rep': 400,
  'end-set': 3000,
  'log-pain': 2000,
  'assisted-mark': 1000,
  'modified-mark': 1000,
};

/** A cluster of taps close enough to be one accidental re-tap. */
export const duplicateTapCandidateSchema = z.object({
  kind: z.enum(TAP_KINDS),
  exerciseCode: exerciseCodeShapeSchema.nullable(),
  /** Clinical-time order, ≥ 2 by construction. */
  tapIds: z.array(z.uuid()).min(2),
  /** First-to-last tap spread of the cluster. */
  spanMs: z.number().int().min(0),
});
export type DuplicateTapCandidate = z.infer<typeof duplicateTapCandidateSchema>;

/**
 * One reconciliation: a corrected tap row superseding the accidental one.
 * Appended (never edited) with provenance 'confirmed-edit' in the same
 * transaction as the sign-off; migration 004's one-shot supersedes seam
 * refuses a second correction of the same row.
 */
export const tapReconciliationSchema = z
  .object({
    supersedesTapId: z.uuid(),
    kind: z.enum(TAP_KINDS),
    exerciseCode: exerciseCodeShapeSchema.optional(),
    tappedAt: z.iso.datetime(),
    detail: z.record(z.string(), z.unknown()).optional(),
    /**
     * How the correction entered (session_taps provenance). Omitted =
     * 'confirmed-edit' (the duplicate-answer path). 'typed-entry' is the D12
     * fallback ONLY: an NRS the physio types at confirmation onto a pain mark
     * that was never scored at tap — a scored restatement of the mark, exactly
     * like the at-tap strip's, arriving through the seal instead.
     */
    provenance: z.enum(['confirmed-edit', 'typed-entry']).optional(),
  })
  .superRefine((t, ctx) => {
    if ((SET_SCOPED_TAP_KINDS as readonly string[]).includes(t.kind) && !t.exerciseCode) {
      ctx.addIssue({
        code: 'custom',
        message: `${t.kind} must name its exercise`,
        path: ['exerciseCode'],
      });
    }
    // An undo is a LIVE act (ADR-0010 extension ¶2). A confirmation-time
    // correction restates the fact it supersedes; negating one is the form's
    // duplicate answer, which lowers the number — never a ledger undo.
    if (t.kind === 'undo') {
      ctx.addIssue({
        code: 'custom',
        message: 'a reconciliation cannot be an undo — it restates the tap it supersedes',
        path: ['kind'],
      });
    }
    if (t.provenance === 'typed-entry') {
      const painScore = t.detail?.['painScore'];
      if (t.kind !== 'log-pain' || painScore === undefined) {
        ctx.addIssue({
          code: 'custom',
          message: 'typed-entry reconciliation is the D12 pain-score fallback only',
          path: ['provenance'],
        });
      } else if (!z.number().int().min(0).max(10).safeParse(painScore).success) {
        ctx.addIssue({
          code: 'custom',
          message: 'painScore is the NRS: a whole number 0–10',
          path: ['detail', 'painScore'],
        });
      }
    }
  });
export type TapReconciliation = z.infer<typeof tapReconciliationSchema>;

// ---- the sign-off request (D4 half: transition now, recomputation is a seam) -

/**
 * POST /api/sessions/:sessionId/confirmation — the seal. One submission
 * carries the whole confirmation: outcomes, any tap reconciliations, and the
 * sign-off act, written atomically. NOT idempotent by design: a retry after a
 * lost response answers `record_already_confirmed` (409) — the form treats
 * that as sealed and re-reads prefill for the confirmed state.
 */
export const confirmSessionRequestSchema = z
  .object({
    /** When the physio signed — client clock, the clinical fact (ADR-0010 §3). */
    occurredAt: z.iso.datetime(),
    /**
     * The device's "now" at send time. The server records
     * (server now − deviceNow) on the sign-off row as the clock-offset
     * observation — bounding every tappedAt in the record.
     */
    deviceNow: z.iso.datetime(),
    outcomes: z.array(exerciseOutcomeInputSchema).min(1).max(50),
    tapReconciliations: z.array(tapReconciliationSchema).max(100).optional(),
    /**
     * Optional session-level clinical note (plan §6), typed at confirmation
     * and sealed on the sign-off row itself — the session-scoped act is its
     * home. Provenance is the act: typed by the signing clinician.
     */
    sessionNote: z.string().min(1).max(2000).optional(),
  })
  .superRefine((r, ctx) => {
    const codes = new Set(r.outcomes.map((o) => o.exerciseCode));
    if (codes.size !== r.outcomes.length) {
      ctx.addIssue({
        code: 'custom',
        message: 'one outcome per exercise — duplicate exerciseCode',
        path: ['outcomes'],
      });
    }
    const superseded = (r.tapReconciliations ?? []).map((t) => t.supersedesTapId);
    if (new Set(superseded).size !== superseded.length) {
      ctx.addIssue({
        code: 'custom',
        message: 'a tap can be superseded at most once',
        path: ['tapReconciliations'],
      });
    }
  });
export type ConfirmSessionRequest = z.infer<typeof confirmSessionRequestSchema>;

// ---- response / read shapes -------------------------------------------------

export const signOffSchema = z.object({
  id: z.uuid(),
  sessionId: z.uuid(),
  /** The authenticated clinician (Supabase user id, ADR-0015 §2). */
  signedOffBy: z.string().min(1),
  occurredAt: z.iso.datetime(),
  recordedAt: z.iso.datetime(),
  /** Server-observed (server now − deviceNow) at sign-off receipt. */
  clockOffsetMs: z.number().int(),
  /** The sealed session-level note. Nullish so older servers' rows parse. */
  sessionNote: z.string().nullish(),
});
export type SignOff = z.infer<typeof signOffSchema>;

export const sessionExerciseOutcomeSchema = z.object({
  id: z.uuid(),
  sessionId: z.uuid(),
  exerciseCode: exerciseCodeShapeSchema,
  outcome: z.enum(EXERCISE_OUTCOMES),
  reason: z.string().nullable(),
  annotationMode: z.enum(ANNOTATION_MODES),
  /** How the outcome choice was made (D4c). Rows sealed pre-010 read hand-set. */
  outcomeProvenance: z.enum(OUTCOME_PROVENANCES),
  /** The proposal shown at confirmation, or null (untapped / pre-010 rows). */
  proposal: tapCountProposalSchema.nullable(),
  confirmedValues: z.record(z.string(), confirmedValueSchema),
  occurredAt: z.iso.datetime(),
  recordedAt: z.iso.datetime(),
  actor: z.string().min(1),
});
export type SessionExerciseOutcome = z.infer<typeof sessionExerciseOutcomeSchema>;

export const confirmSessionResponseSchema = z.object({
  ok: z.literal(true),
  signOff: signOffSchema,
  outcomes: z.array(sessionExerciseOutcomeSchema),
});
export type ConfirmSessionResponse = z.infer<typeof confirmSessionResponseSchema>;

// ---- prefill (D1/D2/D3: from the physio's own taps, and only from them) -----

/**
 * Tap-computed starting values for one planned exercise. Everything here
 * derives from the physio's own tap rows — the server refuses to blend in
 * camera/interpretation-derived numbers (D2: those are reference BESIDE the
 * form, placed by the client, never pre-filled into fields).
 */
export const prefillExerciseSchema = z.object({
  exerciseCode: exerciseCodeShapeSchema,
  name: z.string().min(1),
  plannedSets: z.number().int().min(1),
  plannedReps: z.number().int().min(1),
  /** False = untapped: the form renders BLANK for session-level entry (D3). */
  tapped: z.boolean(),
  /** Null when untapped — never zero, which would be a fabricated answer. */
  setsCompleted: z.number().int().min(0).nullable(),
  repsLogged: z.number().int().min(0).nullable(),
  assistedMarks: z.number().int().min(0).nullable(),
  /** assisted-mark's sibling (IEC v2 parity, PR2). Nullish for older servers. */
  modifiedMarks: z.number().int().min(0).nullish(),
  /** The tap rows behind the numbers above — the form's sourceTapIds. */
  sourceTapIds: z.array(z.uuid()),
  /**
   * B1 — set when today's dosage was tweaked pre-session: plannedSets/Reps
   * above are the PLAN OF RECORD (protocol + tweak); these carry the
   * protocol's own numbers and the physio's rationale so the form can show
   * both. Nullish for untweaked exercises and older servers.
   */
  tweaked: z.boolean().nullish(),
  tweakRationale: z.string().nullish(),
  protocolSets: z.number().int().min(1).nullish(),
  protocolReps: z.number().int().min(1).nullish(),
});
export type PrefillExercise = z.infer<typeof prefillExerciseSchema>;

export const confirmationPrefillResponseSchema = z.object({
  ok: z.literal(true),
  sessionId: z.uuid(),
  sessionStatus: z.enum(['pending', 'live', 'ended', 'dropped']),
  /**
   * C9 — how the session ended, for the record surfaces (sealed card,
   * review header). Absent/null on pre-grammar ends and unended sessions.
   */
  endDisposition: z.enum(SESSION_END_DISPOSITIONS).nullable().optional(),
  endDispositionReason: z.string().nullable().optional(),
  /** True once a sign-off row exists — the form refuses re-entry. */
  signedOff: z.boolean(),
  /**
   * B1 — exercises PAUSED out of today's plan pre-session, with the recorded
   * rationale. Surfaced, never silently dropped: the record shows what was
   * deliberately not planned and why. Nullish for older servers.
   */
  pausedExercises: z
    .array(
      z.object({
        exerciseCode: exerciseCodeShapeSchema,
        name: z.string().min(1),
        rationale: z.string().min(1),
      }),
    )
    .nullish(),
  /**
   * "vs last" (PR6): the episode's PREVIOUS sealed session, for the review
   * step's delta line. Null on the first session, legacy episode-less
   * sessions, and older servers.
   */
  lastSealed: z
    .object({
      sessionId: z.uuid(),
      sealedAt: z.iso.datetime(),
      outcomes: z.array(sessionExerciseOutcomeSchema),
    })
    .nullish(),
  /** Null when the physio never pinned a protocol — surfaced, never inferred. */
  protocolSelection: protocolSelectionSchema.nullable(),
  /** The pinned slot's plan; empty when no pin resolves. */
  plannedExercises: z.array(prefillExerciseSchema),
  /** Set-scoped tap codes OUTSIDE the plan — surfaced, not silently dropped. */
  unplannedTappedExerciseCodes: z.array(exerciseCodeShapeSchema),
  duplicateCandidates: z.array(duplicateTapCandidateSchema),
  /**
   * D11: the sealed record itself, served when (and only when) signedOff — so
   * BOTH read-back paths end on the record: a direct seal renders the confirm
   * response, and a `record_already_confirmed` retry / re-entry renders this.
   * Nullish so older servers' responses still parse.
   */
  sealedRecord: z
    .object({
      signOff: signOffSchema,
      outcomes: z.array(sessionExerciseOutcomeSchema),
    })
    .nullish(),
});
export type ConfirmationPrefillResponse = z.infer<typeof confirmationPrefillResponseSchema>;
